/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * HTML DOM 을 제어한다.
 *
 * @file /src/utils/Html.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 7.
 */
import { Dom } from '@utils/Dom';
import { DomList } from '@utils/DomList';
import { Events } from '@utils/Events';
import { LanguageText } from './Language';

export class Html {
    /**
     * HTML 객체를 생성한다.
     *
     * @param type - 생성할 Node명
     * @param attributes - 포함할 attribute
     * @param html - 포함될 텍스트내용
     * @returns 생성된 DOM 객체
     */
    static create<E extends keyof HTMLElementTagNameMap>(
        type: E,
        attributes: { [key: string]: string } = {},
        html: LanguageText = ''
    ): Dom<HTMLElementTagNameMap[E]> {
        const element = document.createElement(type);
        const dom = Dom.init<HTMLElementTagNameMap[E]>(element);
        for (let key in attributes) {
            dom.setAttr(key, attributes[key]);
        }
        dom.html(html);
        return dom;
    }

    /**
     * HTML 엘리먼트를 Dom 객체로 가져온다.
     *
     * @param element - Dom 객체로 생성할 HTML 엘리먼트 객체
     * @returns 생성된 DOM 객체
     */
    static el<E extends HTMLElement = HTMLElement>(element: HTMLElement | EventTarget): Dom<E> | null {
        if (element instanceof HTMLElement) {
            return Dom.init<E>(element);
        }

        return null;
    }

    /**
     * 쿼리셀렉터에 해당하는 DOM Element 를 가져온다.
     *
     * @param selector - 쿼리셀렉터
     * @param context - DOM Element 를 가져올 부모요소
     * @returns 쿼리셀렉터에 해당하는 DOM 객체
     */
    static get<E extends HTMLElement = HTMLElement>(selector: string, context?: Dom): Dom<E> | null {
        const base = context instanceof Dom ? context.getEl() : document;
        const element = base?.querySelector(Html.selector(selector));
        return Dom.init<E>(element);
    }

    /**
     * HTML 코드를 Dom 객체로 변환한다.
     *
     * @param html
     * @returns 변환된 DOM 객체
     */
    static html<E extends HTMLElement = HTMLElement>(html: string): Dom<E> | null {
        const element = document.createElement(null);
        element.innerHTML = html;

        if (element instanceof HTMLElement) {
            return Html.el<E>(element);
        }

        element.remove();
        return null;
    }

    /**
     * 아이콘 DOM 을 생성한다.
     *
     * @param icon - 아이콘 클래스, 아이콘 URL, 또는 `<svg ...>...</svg>` 마크업
     * @param tagName - 아이콘 태그명 또는 아이콘을 설정할 Dom 객체 (기본값 i)
     * @returns 아이콘 DOM 객체
     */
    static icon<E extends keyof HTMLElementTagNameMap = 'i'>(
        icon: string = null,
        tagName: E | Dom = 'i' as E
    ): Dom<HTMLElementTagNameMap[E]> {
        let $icon: Dom<HTMLElementTagNameMap[E]>;
        if (tagName instanceof Dom) {
            $icon = tagName as Dom<HTMLElementTagNameMap[E]>;
            $icon.removeAttr('class');
            $icon.removeAttr('style');
            $icon.empty();
        } else {
            $icon = Html.create(tagName, { 'data-role': 'icon' });
        }

        if (icon !== null) {
            if (icon.trim().search('<svg') === 0) {
                $icon.html(icon);
            } else if (icon.search('/') === 0 || /(.*?)\.(svg|png|gif|jpg|webp)$/.test(icon) === true) {
                $icon.setStyle('background-image', `url(${icon})`);
            } else {
                $icon.addClass(...icon.split(' '));
            }
        }

        return $icon;
    }

    /**
     * 사진 DOM 을 생성한다.
     *
     * @param url - 사진 URL
     * @param tagName - 사진 태그명 또는 사진을 설정할 Dom 객체 (기본값 i)
     * @returns 사진 DOM 객체
     */
    static photo<E extends keyof HTMLElementTagNameMap = 'i'>(
        url: string = null,
        tagName: E | Dom = 'i' as E
    ): Dom<HTMLElementTagNameMap[E]> {
        let $photo: Dom<HTMLElementTagNameMap[E]>;
        if (tagName instanceof Dom) {
            $photo = tagName as Dom<HTMLElementTagNameMap[E]>;
            $photo.removeAttr('style');
            $photo.empty();
        } else {
            $photo = Html.create(tagName, { 'data-role': 'photo' });
        }

        if (url !== null) {
            $photo.setStyle('background-image', `url(${url})`);
        }

        return $photo;
    }

    /**
     * 쿼리셀렉터 문자열을 표준화한다.
     *
     * @param selector - 쿼리셀렉터
     * @returns 쿼리셀렉터
     */
    static selector(selector: string): string {
        selector = selector.trim();
        if (selector.indexOf('>') === 0) {
            selector = ':scope ' + selector;
        }

        return selector;
    }

    /**
     * 쿼리셀렉터에 해당하는 모든 DOM Element 를 가져온다.
     *
     * @param selector - 쿼리셀렉터
     * @param context - DOM Element 를 가져올 부모요소
     * @returns 선택된 DOM 목록
     */
    static all<E extends HTMLElement = HTMLElement>(selector: string, context?: Dom): DomList {
        let nodes: NodeListOf<Element>;
        if (context instanceof Dom) {
            nodes = context.getEl()?.querySelectorAll(Html.selector(selector));
        } else {
            nodes = document.querySelectorAll(Html.selector(selector));
        }

        return new DomList<E>(nodes);
    }

    /**
     * HTML 문서의 스크롤위치를 조절한다.
     *
     * @param top - 상단위치
     * @param animate - 애니메이션여부
     * @returns 스크롤 위치
     */
    static scrollTop(top?: number, animate: boolean = true): number {
        if (top === undefined) {
            return document.documentElement.scrollTop;
        } else {
            document.documentElement.scroll({ top: top, behavior: animate === true ? 'smooth' : 'auto' });
        }
    }

    /**
     * document 이벤트를 등록한다.
     *
     * @param name - 이벤트명
     * @param listener - 이벤트리스너
     * @param options - 이벤트리스너 옵션
     */
    static on<K extends keyof GlobalEventHandlersEventMap>(
        name: K,
        listener: (e: GlobalEventHandlersEventMap[K]) => any,
        options?: boolean | AddEventListenerOptions
    ): void {
        document.addEventListener(name, listener, options);
    }

    /**
     * document 이벤트를 해제한다.
     *
     * @param name - 이벤트명
     * @param listener - 이벤트리스너
     */
    static off<K extends keyof GlobalEventHandlersEventMap>(
        name: K,
        listener: (e: GlobalEventHandlersEventMap[K]) => any
    ): void {
        document.removeEventListener(name, listener);
    }

    /**
     * HTML 문서의 랜더링 완료 이벤트리스너를 등록한다.
     *
     * @param listener - 이벤트리스너
     */
    static ready(listener: EventListener): void {
        document.addEventListener('DOMContentLoaded', listener);
    }

    /**
     * 순수 텍스트를 HTML 마크업 문자열 안에 안전하게 넣을 수 있도록 이스케이프한다.
     *
     * @param text - 이스케이프할 순수 텍스트
     * @returns 이스케이프된 문자열
     */
    static escape(text: string): string {
        return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    /**
     * HTML 문자열에서 태그를 모두 벗기고 순수 텍스트만 추출한다.
     *
     * @param html - 원본 HTML 문자열
     * @returns 태그가 제거된 순수 텍스트
     */
    static toPlainText(html: string): string {
        const $container = document.createElement('div');
        $container.innerHTML = html;

        $container.querySelectorAll('br').forEach(($br) => {
            $br.replaceWith('\n');
        });

        $container.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, blockquote, tr').forEach(($el) => {
            $el.insertAdjacentText('afterend', '\n');
        });

        return ($container.textContent ?? '').replace(/\n{2,}/g, '\n').trim();
    }

    /**
     * 태그명이 브라우저 기본 스타일시트 상 인라인 레벨인지 판정하기 위한 캐시.
     */
    static #inlineCache: Map<string, boolean> = new Map();

    /**
     * 태그명이 브라우저 기본 스타일시트 상 인라인 레벨인지 판정한다.
     *
     * @param tagName - 판정할 태그명(대소문자 무관)
     * @returns 인라인 레벨이면 true
     */
    static isInlineTag(tagName: string): boolean {
        const key = tagName.toUpperCase();
        if (Html.#inlineCache.has(key) === false) {
            const probe = document.createElement(key.toLowerCase());
            document.body.appendChild(probe);
            Html.#inlineCache.set(key, getComputedStyle(probe).display === 'inline');
            probe.remove();
        }

        return Html.#inlineCache.get(key)!;
    }

    /**
     * HTML 문자열에서 허용된 태그와 그 태그의 허용된 속성만 남기고 나머지는 벗겨낸다.
     *
     * @param html - 원본 HTML 문자열
     * @param tags - 그대로 남길 태그명 목록
     * @param attrs - 남긴 태그에서 그대로 남길 속성명 목록
     * @returns 허용된 태그/속성만 남은 HTML 문자열
     */
    static stripHtml(html: string, tags: string[] = [], attrs: string[] = []): string {
        const tagSet = new Set(tags.map((tag) => tag.toUpperCase()));
        const attrSet = new Set(attrs.map((attr) => attr.toLowerCase()));

        const strip = (parent: HTMLElement): void => {
            for (const child of [...parent.childNodes]) {
                if (child.nodeType !== Node.ELEMENT_NODE) {
                    continue;
                }

                const el = child as HTMLElement;
                strip(el);

                if (tagSet.has(el.tagName) === true) {
                    for (const attr of [...el.attributes]) {
                        if (attrSet.has(attr.name.toLowerCase()) === false) {
                            el.removeAttribute(attr.name);
                        }
                    }
                    continue;
                }

                if (Html.isInlineTag(el.tagName) === false) {
                    const prev = el.previousSibling;
                    const hasBreak = prev === null || (prev as HTMLElement).tagName === 'BR';
                    if (hasBreak === false) {
                        parent.insertBefore(document.createElement('br'), el);
                    }
                }

                while (el.firstChild !== null) {
                    parent.insertBefore(el.firstChild, el);
                }
                el.remove();
            }
        };

        const $container = document.createElement('div');
        $container.innerHTML = html;
        strip($container);

        return $container.innerHTML;
    }
}

Events.init();
